import React, { useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Badge,
    Button,
    Card,
    Col,
    Empty,
    Grid,
    List,
    Row,
    Select,
    Space,
    Statistic,
    Switch,
    Table,
    Tag,
    Tooltip,
    Typography,
} from 'antd';
import {
    RadarChartOutlined,
    ReloadOutlined,
    EnvironmentOutlined,
    LinkOutlined,
    CloseOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { FormattedMessage, useIntl } from 'react-intl';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
    Bar,
    BarChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip as ChartTooltip,
    XAxis,
    YAxis,
} from 'recharts';
import { useGetLiveAnalyticsQuery } from '../../services/nestjs-base-api';
import {
    ILiveEvent,
    ILiveVisitor,
    LiveMilestone,
    LiveSurface,
} from '../../services/nestjs-base-api/types';
import { useAuth } from '../../hooks/useAuth';

dayjs.extend(relativeTime);

const { Title, Text } = Typography;

const PRIMARY = '#18216d';
const ACTIVE_GREEN = '#52c41a';

const WINDOW_OPTIONS = [15, 30, 60, 120];
// 10 s is safe: the API caches each snapshot for 10 s, so however many tabs
// are open PostHog sees at most 6 queries a minute against a 2400/hour budget.
const REFRESH_OPTIONS = [0, 10, 15, 30, 60];
const DEFAULT_REFRESH_SECONDS = 15;

// The three controls persist per browser so the page reopens the way it was
// left during a launch watch.
const SETTINGS_KEY = 'admin.liveAnalytics.settings';

interface LiveSettings {
    windowMinutes: number;
    refreshSeconds: number;
    includeTeam: boolean;
}

const DEFAULT_SETTINGS: LiveSettings = { windowMinutes: 30, refreshSeconds: DEFAULT_REFRESH_SECONDS, includeTeam: false };

const loadSettings = (): LiveSettings => {
    try {
        const raw = window.localStorage.getItem(SETTINGS_KEY);
        if (!raw) return DEFAULT_SETTINGS;
        const parsed = JSON.parse(raw);
        return {
            windowMinutes: WINDOW_OPTIONS.includes(parsed.windowMinutes) ? parsed.windowMinutes : DEFAULT_SETTINGS.windowMinutes,
            refreshSeconds: REFRESH_OPTIONS.includes(parsed.refreshSeconds) ? parsed.refreshSeconds : DEFAULT_SETTINGS.refreshSeconds,
            includeTeam: typeof parsed.includeTeam === 'boolean' ? parsed.includeTeam : DEFAULT_SETTINGS.includeTeam,
        };
    } catch {
        return DEFAULT_SETTINGS;
    }
};

const SURFACE_COLORS: Record<LiveSurface, string> = {
    website: 'blue',
    app: 'purple',
    survey: 'green',
    server: 'gold',
    other: 'default',
};

const MILESTONE_COLORS: Record<LiveMilestone, string> = {
    signup_cta: 'orange',
    signed_up: 'gold',
    logged_in: 'green',
    hypothesis_requested: 'geekblue',
    hypothesis_verified: 'geekblue',
    hypothesis_ready: 'cyan',
    hypothesis_viewed: 'cyan',
    hypothesis_cta: 'magenta',
    survey_submitted: 'green',
    charter_applied: 'gold',
};

// Platform rows are the half PostHog cannot see, so they get their own colour
// family in the stream; clickstream rows take the surface colour.
const PLATFORM_EVENT_COLORS: Record<string, string> = {
    USER_REGISTERED: 'gold',
    ORGANIZATION_CREATED: 'gold',
    PAID_SIGNUP_COMPLETED: 'gold',
    CHARTER_ORGANISATION_PROVISIONED: 'gold',
    USER_LOGIN: 'green',
    USER_LOGIN_FAILED: 'red',
    EMAIL_DELIVERED: 'cyan',
    EMAIL_OPENED: 'green',
    EMAIL_CLICKED: 'magenta',
    EMAIL_BOUNCED: 'red',
    SURVEY_RESPONSE_RECEIVED: 'green',
    PUBLIC_HYPOTHESIS_FAILED: 'red',
    PUBLIC_HYPOTHESIS_SPAM_REJECTED: 'red',
    PUBLIC_HYPOTHESIS_BLOCKED: 'red',
};

const flagFor = (countryCode: string | null): string => {
    if (!countryCode || countryCode.length !== 2) return '';
    const base = 0x1f1e6;
    const upper = countryCode.toUpperCase();
    return String.fromCodePoint(
        base + upper.charCodeAt(0) - 65,
        base + upper.charCodeAt(1) - 65,
    );
};

const locationLabel = (
    country: string | null,
    region: string | null,
    city: string | null,
): string | null => {
    const parts = [city, region && region !== city ? region : null, country].filter(Boolean);
    return parts.length ? parts.join(', ') : null;
};

// Raw event names from either source, made readable.
const eventLabel = (event: string): string => {
    if (event === '$pageview') return 'page view';
    if (event.startsWith('$')) return event.slice(1).replace(/_/g, ' ');
    if (/^[A-Z][A-Z0-9_]+$/.test(event)) return event.toLowerCase().replace(/_/g, ' ');
    return event.replace(/_/g, ' ');
};

const SurfaceTag: React.FC<{ surface: LiveSurface }> = ({ surface }) => (
    <Tag color={SURFACE_COLORS[surface]} style={{ marginRight: 0 }}>
        <FormattedMessage id={`admin.live.surface.${surface}`} />
    </Tag>
);

const VisitorName: React.FC<{ visitor: ILiveVisitor }> = ({ visitor }) => {
    if (visitor.identityKind === 'anonymous') {
        return (
            <Text type="secondary">
                <FormattedMessage id="admin.live.visitors.anonymous" />
            </Text>
        );
    }
    return (
        <Space direction="vertical" size={0}>
            <Text strong>{visitor.identityLabel || visitor.identity}</Text>
            {visitor.identityLabel && visitor.identity && visitor.identityLabel !== visitor.identity && (
                <Text type="secondary" style={{ fontSize: 12 }}>{visitor.identity}</Text>
            )}
            {visitor.organisationName && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {visitor.organisationId ? (
                        <Link to={`/admin/organizations/${visitor.organisationId}`}>{visitor.organisationName}</Link>
                    ) : visitor.organisationName}
                </Text>
            )}
        </Space>
    );
};

export const AdminLiveAnalyticsPage: React.FC = () => {
    const intl = useIntl();
    const { user } = useAuth();
    const screens = Grid.useBreakpoint();
    const isMobile = !screens.md;
    const [initialSettings] = useState<LiveSettings>(loadSettings);
    const [windowMinutes, setWindowMinutes] = useState(initialSettings.windowMinutes);
    const [refreshSeconds, setRefreshSeconds] = useState(initialSettings.refreshSeconds);
    const [includeTeam, setIncludeTeam] = useState(initialSettings.includeTeam);
    const [selectedVisitorKey, setSelectedVisitorKey] = useState<string | null>(null);
    const [now, setNow] = useState(() => Date.now());

    const { data, error, isFetching, refetch } = useGetLiveAnalyticsQuery(
        { jwtToken: user?.jwtToken || '', minutes: windowMinutes },
        {
            skip: !user?.jwtToken,
            pollingInterval: refreshSeconds > 0 ? refreshSeconds * 1000 : 0,
            refetchOnFocus: true,
        },
    );

    useEffect(() => {
        try {
            window.localStorage.setItem(SETTINGS_KEY, JSON.stringify({ windowMinutes, refreshSeconds, includeTeam }));
        } catch {
            // Storage can be unavailable (private mode quotas); the page still works.
        }
    }, [windowMinutes, refreshSeconds, includeTeam]);

    // A ticking clock so "updated 12s ago" and the Active/Idle dots move
    // between polls rather than only when new data lands.
    useEffect(() => {
        const timer = window.setInterval(() => setNow(Date.now()), 1000);
        return () => window.clearInterval(timer);
    }, []);

    const visitors = useMemo(
        () => (data?.visitors || []).filter((v) => includeTeam || !v.isTeam),
        [data, includeTeam],
    );
    // Testing the page by visiting the site yourself lands you in this bucket,
    // so say so rather than showing an empty room.
    const hiddenTeamCount = includeTeam ? 0 : (data?.visitors || []).filter((v) => v.isTeam).length;
    const visitorByKey = useMemo(
        () => new Map(visitors.map((v) => [v.key, v])),
        [visitors],
    );
    const selectedVisitor = selectedVisitorKey ? visitorByKey.get(selectedVisitorKey) ?? null : null;

    const events = useMemo(() => {
        const all = (data?.events || []).filter((e) => includeTeam || !e.isTeam);
        return selectedVisitorKey ? all.filter((e) => e.visitorKey === selectedVisitorKey) : all;
    }, [data, includeTeam, selectedVisitorKey]);

    // Counts follow the team toggle, so they always describe the list below.
    const activeCutoff = now - (data?.activeWindowMinutes ?? 5) * 60_000;
    const activeNow = visitors.filter((v) => dayjs(v.lastSeen).valueOf() >= activeCutoff).length;
    const pageviews = visitors.reduce((sum, v) => sum + v.pageviewCount, 0);

    const chartData = useMemo(
        () => (data?.perMinute || []).map((p) => ({
            ...p,
            label: dayjs(p.minute).format('HH:mm'),
        })),
        [data],
    );

    const updatedAgo = data ? dayjs(data.generatedAt).from(dayjs(now)) : null;

    const visitorColumns: ColumnsType<ILiveVisitor> = [
        {
            title: <FormattedMessage id="admin.live.visitors.col.visitor" />,
            key: 'visitor',
            width: 260,
            render: (_, v) => {
                const isActive = dayjs(v.lastSeen).valueOf() >= activeCutoff;
                return (
                    <Space align="start">
                        <Tooltip title={intl.formatMessage({ id: isActive ? 'admin.live.visitors.active' : 'admin.live.visitors.idle' })}>
                            <Badge status={isActive ? 'processing' : 'default'} color={isActive ? ACTIVE_GREEN : undefined} />
                        </Tooltip>
                        <Space direction="vertical" size={2}>
                            <VisitorName visitor={v} />
                            <Space size={4} wrap>
                                {v.isTeam && <Tag style={{ marginRight: 0 }}><FormattedMessage id="admin.live.visitors.team" /></Tag>}
                                {v.identityKind === 'lead' && <Tag color="geekblue" style={{ marginRight: 0 }}><FormattedMessage id="admin.live.visitors.lead" /></Tag>}
                                {v.identityKind === 'user' && <Tag color="purple" style={{ marginRight: 0 }}><FormattedMessage id="admin.live.visitors.user" /></Tag>}
                                {v.surfaces.filter((s) => s !== 'server').map((s) => <SurfaceTag key={s} surface={s} />)}
                            </Space>
                        </Space>
                    </Space>
                );
            },
        },
        {
            title: <FormattedMessage id="admin.live.visitors.col.location" />,
            key: 'location',
            width: 200,
            render: (_, v) => {
                const label = locationLabel(v.country, v.region, v.city);
                return (
                    <Space direction="vertical" size={0}>
                        <Text>{flagFor(v.countryCode)} {label || <Text type="secondary"><FormattedMessage id="admin.live.unknownLocation" /></Text>}</Text>
                        {(v.device || v.browser) && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {[v.device, v.browser, v.os].filter(Boolean).join(' · ')}
                            </Text>
                        )}
                    </Space>
                );
            },
        },
        {
            title: <FormattedMessage id="admin.live.visitors.col.nowOn" />,
            key: 'nowOn',
            render: (_, v) => (
                <Space direction="vertical" size={2}>
                    <SurfaceTag surface={v.currentSurface} />
                    <Text code style={{ fontSize: 12 }}>{v.currentPath || '/'}</Text>
                </Space>
            ),
        },
        {
            title: <FormattedMessage id="admin.live.visitors.col.source" />,
            key: 'source',
            width: 180,
            render: (_, v) => (
                <Space direction="vertical" size={0}>
                    <Text>{v.referringDomain && v.referringDomain !== '$direct' ? v.referringDomain : <FormattedMessage id="admin.live.direct" />}</Text>
                    {(v.utmSource || v.utmCampaign) && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {[v.utmSource, v.utmMedium, v.utmCampaign].filter(Boolean).join(' / ')}
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: <FormattedMessage id="admin.live.visitors.col.milestones" />,
            key: 'milestones',
            render: (_, v) => (
                <Space size={4} wrap>
                    {v.milestones.map((m) => (
                        <Tag key={m} color={MILESTONE_COLORS[m]} style={{ marginRight: 0 }}>
                            <FormattedMessage id={`admin.live.milestone.${m}`} />
                        </Tag>
                    ))}
                </Space>
            ),
        },
        {
            title: <FormattedMessage id="admin.live.visitors.col.journey" />,
            key: 'journey',
            width: 150,
            render: (_, v) => (
                <Space direction="vertical" size={0}>
                    <Text><FormattedMessage id="admin.live.visitors.pages" values={{ count: v.pageviewCount }} /></Text>
                    {v.platformEventCount > 0 && (
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <FormattedMessage id="admin.live.visitors.platformEvents" values={{ count: v.platformEventCount }} />
                        </Text>
                    )}
                </Space>
            ),
        },
        {
            title: <FormattedMessage id="admin.live.visitors.col.lastSeen" />,
            key: 'lastSeen',
            width: 130,
            render: (_, v) => (
                <Tooltip title={dayjs(v.lastSeen).format('YYYY-MM-DD HH:mm:ss')}>
                    <Text>{dayjs(v.lastSeen).from(dayjs(now))}</Text>
                </Tooltip>
            ),
        },
    ];

    // On a phone the seven columns become one stacked card per visitor, so
    // nothing needs sideways scrolling to be read.
    const mobileVisitorColumns: ColumnsType<ILiveVisitor> = [
        {
            title: <FormattedMessage id="admin.live.visitors.col.visitor" />,
            key: 'visitor',
            render: (_, v) => {
                const isActive = dayjs(v.lastSeen).valueOf() >= activeCutoff;
                const label = locationLabel(v.country, v.region, v.city);
                return (
                    <Space direction="vertical" size={4} style={{ width: '100%' }}>
                        <Space align="start">
                            <Badge status={isActive ? 'processing' : 'default'} color={isActive ? ACTIVE_GREEN : undefined} />
                            <VisitorName visitor={v} />
                        </Space>
                        <Space size={4} wrap>
                            {v.isTeam && <Tag style={{ marginRight: 0 }}><FormattedMessage id="admin.live.visitors.team" /></Tag>}
                            {v.identityKind === 'lead' && <Tag color="geekblue" style={{ marginRight: 0 }}><FormattedMessage id="admin.live.visitors.lead" /></Tag>}
                            {v.identityKind === 'user' && <Tag color="purple" style={{ marginRight: 0 }}><FormattedMessage id="admin.live.visitors.user" /></Tag>}
                            <SurfaceTag surface={v.currentSurface} />
                            <Text code style={{ fontSize: 12 }}>{v.currentPath || '/'}</Text>
                        </Space>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            {flagFor(v.countryCode)} {label || intl.formatMessage({ id: 'admin.live.unknownLocation' })}
                            {' · '}
                            {v.referringDomain && v.referringDomain !== '$direct' ? v.referringDomain : intl.formatMessage({ id: 'admin.live.direct' })}
                            {' · '}
                            {dayjs(v.lastSeen).from(dayjs(now))}
                        </Text>
                        {v.milestones.length > 0 && (
                            <Space size={4} wrap>
                                {v.milestones.map((m) => (
                                    <Tag key={m} color={MILESTONE_COLORS[m]} style={{ marginRight: 0 }}>
                                        <FormattedMessage id={`admin.live.milestone.${m}`} />
                                    </Tag>
                                ))}
                            </Space>
                        )}
                    </Space>
                );
            },
        },
    ];

    const renderVisitorDetail = (v: ILiveVisitor) => {
        const platformEvents = (data?.events || []).filter(
            (e) => e.source === 'platform' && e.visitorKey === v.key,
        );
        return (
            <Row gutter={24}>
                <Col xs={24} md={12}>
                    <Text strong><FormattedMessage id="admin.live.visitors.journeyTitle" /></Text>
                    <List
                        size="small"
                        dataSource={v.journey}
                        renderItem={(hit) => (
                            <List.Item style={{ padding: '4px 0' }}>
                                <Space>
                                    <Text type="secondary" style={{ fontSize: 12, width: 64, display: 'inline-block' }}>
                                        {dayjs(hit.timestamp).format('HH:mm:ss')}
                                    </Text>
                                    <SurfaceTag surface={hit.surface} />
                                    <Text code style={{ fontSize: 12 }}>{hit.path}</Text>
                                </Space>
                            </List.Item>
                        )}
                    />
                    <Space direction="vertical" size={0} style={{ marginTop: 8 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            <FormattedMessage id="admin.live.visitors.firstSeen" />: {dayjs(v.firstSeen).format('HH:mm:ss')}
                        </Text>
                        {v.utmCampaign && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                <FormattedMessage id="admin.live.visitors.campaign" />: {[v.utmSource, v.utmMedium, v.utmCampaign].filter(Boolean).join(' / ')}
                            </Text>
                        )}
                    </Space>
                </Col>
                <Col xs={24} md={12}>
                    <Text strong><FormattedMessage id="admin.live.visitors.platformTitle" /></Text>
                    {platformEvents.length === 0 ? (
                        <div style={{ marginTop: 8 }}>
                            <Text type="secondary"><FormattedMessage id="admin.live.visitors.noPlatformEvents" /></Text>
                        </div>
                    ) : (
                        <List
                            size="small"
                            dataSource={platformEvents}
                            renderItem={(e) => (
                                <List.Item style={{ padding: '4px 0' }}>
                                    <Space>
                                        <Text type="secondary" style={{ fontSize: 12, width: 64, display: 'inline-block' }}>
                                            {dayjs(e.timestamp).format('HH:mm:ss')}
                                        </Text>
                                        <Tag color={PLATFORM_EVENT_COLORS[e.event] || 'default'} style={{ marginRight: 0 }}>{eventLabel(e.event)}</Tag>
                                        {e.detail && <Text type="secondary" style={{ fontSize: 12 }}>{e.detail}</Text>}
                                    </Space>
                                </List.Item>
                            )}
                        />
                    )}
                </Col>
            </Row>
        );
    };

    const renderStreamEvent = (e: ILiveEvent) => {
        const visitor = e.visitorKey ? visitorByKey.get(e.visitorKey) : undefined;
        const who = visitor?.identityLabel || visitor?.identity || e.identity;
        const isPlatform = e.source === 'platform';
        return (
            <List.Item
                style={{ padding: '6px 0', cursor: e.visitorKey ? 'pointer' : 'default' }}
                onClick={() => e.visitorKey && setSelectedVisitorKey(e.visitorKey)}
            >
                <Space align="start" style={{ width: '100%' }}>
                    <Text type="secondary" style={{ fontSize: 12, width: 64, display: 'inline-block' }}>
                        {dayjs(e.timestamp).format('HH:mm:ss')}
                    </Text>
                    {isPlatform ? (
                        <Tag color={PLATFORM_EVENT_COLORS[e.event] || 'default'} style={{ marginRight: 0 }}>
                            <FormattedMessage id="admin.live.stream.platform" /> · {eventLabel(e.event)}
                        </Tag>
                    ) : (
                        <Tag color={SURFACE_COLORS[e.surface]} style={{ marginRight: 0 }}>
                            <FormattedMessage id={`admin.live.surface.${e.surface}`} /> · {eventLabel(e.event)}
                        </Tag>
                    )}
                    <Space direction="vertical" size={0}>
                        <Space size={6} wrap>
                            {who ? <Text strong>{who}</Text> : <Text type="secondary"><FormattedMessage id="admin.live.visitors.anonymous" /></Text>}
                            {e.organisationName && <Text type="secondary">· {e.organisationName}</Text>}
                            {(e.city || e.country) && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    <EnvironmentOutlined style={{ marginRight: 4 }} />
                                    {[e.city, e.country].filter(Boolean).join(', ')}
                                </Text>
                            )}
                        </Space>
                        {(e.path || e.detail) && (
                            <Text type="secondary" style={{ fontSize: 12 }}>
                                {e.path && <Text code style={{ fontSize: 12 }}>{e.path}</Text>}
                                {e.path && e.detail ? ' ' : ''}
                                {e.detail}
                            </Text>
                        )}
                    </Space>
                </Space>
            </List.Item>
        );
    };

    return (
        <div style={{ padding: 24 }}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
                <Row justify="space-between" align="middle" gutter={[16, 16]}>
                    <Col>
                        <Title level={3} style={{ margin: 0 }}>
                            <RadarChartOutlined style={{ marginRight: 8 }} />
                            <FormattedMessage id="admin.live.title" />
                        </Title>
                        <Text type="secondary"><FormattedMessage id="admin.live.subtitle" /></Text>
                        <br />
                        <Text type="secondary" style={{ fontSize: 12 }}><FormattedMessage id="admin.live.lagNote" /></Text>
                    </Col>
                    <Col>
                        <Space wrap>
                            <Space size={6}>
                                <Switch size="small" checked={includeTeam} onChange={setIncludeTeam} />
                                <Text type="secondary"><FormattedMessage id="admin.live.includeTeam" /></Text>
                            </Space>
                            <Select
                                value={windowMinutes}
                                style={{ width: 150 }}
                                onChange={setWindowMinutes}
                                options={WINDOW_OPTIONS.map((m) => ({
                                    value: m,
                                    label: intl.formatMessage({ id: 'admin.live.windowMinutes' }, { minutes: m }),
                                }))}
                            />
                            <Select
                                value={refreshSeconds}
                                style={{ width: 120 }}
                                onChange={setRefreshSeconds}
                                options={REFRESH_OPTIONS.map((s) => ({
                                    value: s,
                                    label: s === 0
                                        ? intl.formatMessage({ id: 'admin.live.refreshOff' })
                                        : intl.formatMessage({ id: 'admin.live.refreshEvery' }, { seconds: s }),
                                }))}
                            />
                            <Button icon={<ReloadOutlined />} onClick={() => refetch()} loading={isFetching}>
                                <FormattedMessage id="admin.live.refresh" />
                            </Button>
                            {updatedAgo && (
                                <Text type="secondary" style={{ fontSize: 12 }}>
                                    <FormattedMessage id="admin.live.updatedAgo" values={{ ago: updatedAgo }} />
                                </Text>
                            )}
                        </Space>
                    </Col>
                </Row>

                {error && <Alert type="error" showIcon message={<FormattedMessage id="admin.live.error" />} />}
                {data && !data.enabled && (
                    <Alert
                        type="info"
                        showIcon
                        message={<FormattedMessage id="admin.live.disabled" />}
                        description={<FormattedMessage id="admin.live.disabledHelp" />}
                    />
                )}
                {hiddenTeamCount > 0 && (
                    <Alert
                        type="info"
                        showIcon
                        message={<FormattedMessage id="admin.live.teamHidden" values={{ count: hiddenTeamCount }} />}
                        action={
                            <Button size="small" onClick={() => setIncludeTeam(true)}>
                                <FormattedMessage id="admin.live.teamHiddenShow" />
                            </Button>
                        }
                    />
                )}

                <Row gutter={[16, 16]}>
                    <Col xs={24} sm={12} lg={4}>
                        <Card size={isMobile ? 'small' : 'default'}>
                            <Statistic
                                title={
                                    <Tooltip title={intl.formatMessage({ id: 'admin.live.stat.activeNowHelp' }, { minutes: data?.activeWindowMinutes ?? 5 })}>
                                        <span>
                                            <Badge status="processing" color={ACTIVE_GREEN} style={{ marginRight: 6 }} />
                                            <FormattedMessage id="admin.live.stat.activeNow" />
                                        </span>
                                    </Tooltip>
                                }
                                value={activeNow}
                                valueStyle={{ color: activeNow > 0 ? ACTIVE_GREEN : undefined, fontSize: 36 }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={12} lg={5}>
                        <Card size={isMobile ? 'small' : 'default'}><Statistic title={<FormattedMessage id="admin.live.stat.visitors" />} value={visitors.length} /></Card>
                    </Col>
                    <Col xs={12} sm={12} lg={5}>
                        <Card size={isMobile ? 'small' : 'default'}><Statistic title={<FormattedMessage id="admin.live.stat.pageviews" />} value={pageviews} /></Card>
                    </Col>
                    <Col xs={12} sm={12} lg={5}>
                        <Card size={isMobile ? 'small' : 'default'}>
                            <Statistic
                                title={<FormattedMessage id="admin.live.stat.signups" />}
                                value={data?.signupsInWindow ?? 0}
                                valueStyle={{ color: (data?.signupsInWindow ?? 0) > 0 ? '#d48806' : undefined }}
                            />
                        </Card>
                    </Col>
                    <Col xs={12} sm={12} lg={5}>
                        <Card size={isMobile ? 'small' : 'default'}><Statistic title={<FormattedMessage id="admin.live.stat.hypothesis" />} value={data?.hypothesisRequestsInWindow ?? 0} /></Card>
                    </Col>
                </Row>

                <Card title={<FormattedMessage id="admin.live.chart.title" />} styles={{ body: { paddingTop: 8 } }}>
                    <div style={{ width: '100%', height: 160 }}>
                        <ResponsiveContainer>
                            <BarChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }} barCategoryGap={2}>
                                <CartesianGrid vertical={false} stroke="#f0f0f0" />
                                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#8c8c8c' }} tickLine={false} axisLine={false} interval="preserveStartEnd" minTickGap={24} />
                                <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#8c8c8c' }} tickLine={false} axisLine={false} width={40} />
                                <ChartTooltip
                                    cursor={{ fill: 'rgba(24, 33, 109, 0.06)' }}
                                    formatter={(value: any, name: any) => [
                                        value,
                                        intl.formatMessage({ id: name === 'visitors' ? 'admin.live.chart.visitors' : 'admin.live.chart.pageviews' }),
                                    ]}
                                />
                                <Bar dataKey="visitors" fill={PRIMARY} radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </Card>

                <Row gutter={[16, 16]} className="live-analytics-summary">
                    <Col xs={24} lg={10}>
                        <Card title={<FormattedMessage id="admin.live.topPages" />} size="small" styles={{ body: { padding: '0 12px' } }}>
                            <List
                                size="small"
                                dataSource={data?.topPages.slice(0, 10) || []}
                                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={intl.formatMessage({ id: 'admin.live.visitors.empty' })} /> }}
                                renderItem={(p) => (
                                    <List.Item style={{ padding: '6px 0' }}>
                                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                            <Space size={6}>
                                                <SurfaceTag surface={p.surface} />
                                                <Text code style={{ fontSize: 12 }}>{p.path}</Text>
                                            </Space>
                                            <Text strong>{p.visitors}</Text>
                                        </Space>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} md={12} lg={7}>
                        <Card title={<FormattedMessage id="admin.live.locations" />} size="small" styles={{ body: { padding: '0 12px' } }}>
                            <List
                                size="small"
                                dataSource={data?.locations.slice(0, 10) || []}
                                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={intl.formatMessage({ id: 'admin.live.visitors.empty' })} /> }}
                                renderItem={(l) => (
                                    <List.Item style={{ padding: '6px 0' }}>
                                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                            <Text>
                                                {flagFor(l.countryCode)} {[l.city, l.country].filter(Boolean).join(', ') || <FormattedMessage id="admin.live.unknownLocation" />}
                                            </Text>
                                            <Text strong>{l.visitors}</Text>
                                        </Space>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                    <Col xs={24} md={12} lg={7}>
                        <Card title={<FormattedMessage id="admin.live.sources" />} size="small" styles={{ body: { padding: '0 12px' } }}>
                            <List
                                size="small"
                                dataSource={data?.sources.slice(0, 10) || []}
                                locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={intl.formatMessage({ id: 'admin.live.visitors.empty' })} /> }}
                                renderItem={(s) => (
                                    <List.Item style={{ padding: '6px 0' }}>
                                        <Space style={{ width: '100%', justifyContent: 'space-between' }}>
                                            <Text>
                                                <LinkOutlined style={{ marginRight: 6, color: '#8c8c8c' }} />
                                                {s.referringDomain === '$direct' ? <FormattedMessage id="admin.live.direct" /> : s.referringDomain}
                                            </Text>
                                            <Text strong>{s.visitors}</Text>
                                        </Space>
                                    </List.Item>
                                )}
                            />
                        </Card>
                    </Col>
                </Row>

                <Card title={<FormattedMessage id="admin.live.visitors.title" />} size="small" className="live-analytics-summary" styles={{ body: { padding: 0 } }}>
                    <Table<ILiveVisitor>
                        rowKey="key"
                        size="small"
                        columns={isMobile ? mobileVisitorColumns : visitorColumns}
                        dataSource={visitors}
                        pagination={visitors.length > 25 ? { pageSize: 25, size: 'small' } : false}
                        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={intl.formatMessage({ id: 'admin.live.visitors.empty' })} /> }}
                        expandable={{ expandedRowRender: renderVisitorDetail }}
                        rowClassName={(v) => (v.key === selectedVisitorKey ? 'ant-table-row-selected' : '')}
                        onRow={(v) => ({
                            onClick: () => setSelectedVisitorKey(v.key === selectedVisitorKey ? null : v.key),
                            style: { cursor: 'pointer' },
                        })}
                        scroll={isMobile ? undefined : { x: 1000 }}
                    />
                </Card>

                <Card
                    title={<FormattedMessage id="admin.live.stream.title" />}
                    size="small"
                    extra={selectedVisitor && (
                        <Space>
                            <Text type="secondary">
                                <FormattedMessage
                                    id="admin.live.stream.filteredBy"
                                    values={{ visitor: selectedVisitor.identityLabel || selectedVisitor.identity || intl.formatMessage({ id: 'admin.live.visitors.anonymous' }) }}
                                />
                            </Text>
                            <Button size="small" icon={<CloseOutlined />} onClick={() => setSelectedVisitorKey(null)}>
                                <FormattedMessage id="admin.live.stream.showAll" />
                            </Button>
                        </Space>
                    )}
                    styles={{ body: { padding: '0 12px', maxHeight: 560, overflowY: 'auto' } }}
                >
                    <List
                        size="small"
                        dataSource={events}
                        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={intl.formatMessage({ id: 'admin.live.stream.empty' })} /> }}
                        renderItem={renderStreamEvent}
                    />
                </Card>
            </Space>
        </div>
    );
};
